import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { fetchPacingHolds, cancelPacingHold } from '@src/api/actions';
import { useAuth } from '@src/context';

const TIER_COLOR = {
  slow_down: 'warning',
  minimal_spend: 'error',
  boost: 'success',
};

function formatDate(s) {
  if (!s) return '-';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Internal-only read-out of every currently-active pacing adjustment across all
 * clients. Used for oversight — auto-apply means client submissions take effect
 * within ~15 minutes without staff approval, so this is the bird's-eye view
 * staff scan daily to catch anything weird.
 */
export default function ActiveHoldsAdminPage() {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;

  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchPacingHolds({ active: 'true' })
      .then((res) => setHolds(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed to load active holds.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isStaff) load();
  }, [isStaff, load]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const h of holds) {
      const key = h.client;
      if (!map.has(key)) {
        map.set(key, { client_id: key, client_name: h.client_name || `Client #${key}`, holds: [] });
      }
      map.get(key).holds.push(h);
    }
    return Array.from(map.values()).sort((a, b) => a.client_name.localeCompare(b.client_name));
  }, [holds]);

  const tierCounts = useMemo(() => {
    const counts = { slow_down: 0, minimal_spend: 0, boost: 0 };
    for (const h of holds) counts[h.tier] = (counts[h.tier] || 0) + 1;
    return counts;
  }, [holds]);

  const handleCancel = (id, { cancelBatch = false, territoryCount = 1 } = {}) => {
    const message = cancelBatch && territoryCount > 1
      ? `Cancel this batch of ${territoryCount} territories? All will revert on the next optimizer run.`
      : 'Cancel this adjustment? The client will see it removed and the optimizer will revert this scope.';
    if (!window.confirm(message)) return;
    cancelPacingHold(id, { cancelBatch }).then(() => load()).catch((err) => setError(err?.message || 'Cancel failed.'));
  };

  function groupHoldsByBatch(rows) {
    const groups = [];
    const byBatch = new Map();
    for (const h of rows) {
      if (!h.batch_id) { groups.push({ key: `s-${h.id}`, lead: h, members: [h] }); continue; }
      if (!byBatch.has(h.batch_id)) {
        const g = { key: `b-${h.batch_id}`, lead: h, members: [] };
        byBatch.set(h.batch_id, g);
        groups.push(g);
      }
      byBatch.get(h.batch_id).members.push(h);
    }
    return groups;
  }

  if (!isStaff) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">This page is staff-only.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5">Active Pacing Holds (All Clients)</Typography>
          <Typography variant="body2" color="text.secondary">
            Every adjustment currently shaping the optimizer. Client submissions auto-apply within ~15 minutes — this view is for spotting anything that looks wrong.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${holds.length} active`} variant="outlined" />
          <Chip label={`${tierCounts.boost || 0} boost`} color="success" variant="outlined" />
          <Chip label={`${tierCounts.slow_down || 0} slow down`} color="warning" variant="outlined" />
          <Chip label={`${tierCounts.minimal_spend || 0} minimal spend`} color="error" variant="outlined" />
        </Stack>

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : holds.length === 0 ? (
          <Alert severity="info">No active adjustments across any client.</Alert>
        ) : (
          grouped.map((g) => (
            <Card key={g.client_id}>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>{g.client_name}</Typography>
                    <Chip size="small" label={`${g.holds.length} active`} />
                  </Stack>
                }
                action={
                  <Button
                    size="small"
                    component={RouterLink}
                    to={`/clients/${g.client_id}/pacing-control`}
                  >
                    Open client page
                  </Button>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tier</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Dates</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Requested by</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupHoldsByBatch(g.holds).map((grp) => {
                        const h = grp.lead;
                        const isBatch = grp.members.length > 1;
                        const territoryNames = grp.members.map((m) => m.scope_label || 'Territory');
                        return (
                          <TableRow key={grp.key}>
                            <TableCell>
                              <Chip
                                size="small"
                                label={h.tier_label || h.tier}
                                color={TIER_COLOR[h.tier] || 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {isBatch ? (
                                <>
                                  <Typography variant="body2" fontWeight={500}>{grp.members.length} territories</Typography>
                                  <Typography variant="caption" color="text.secondary">{territoryNames.join(', ')}</Typography>
                                </>
                              ) : (
                                <>
                                  <Typography variant="body2" fontWeight={500}>{h.scope_label || h.scope_type_label}</Typography>
                                  <Typography variant="caption" color="text.secondary">{h.scope_type_label}</Typography>
                                </>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(h.start_date)} → {formatDate(h.end_date)}</Typography>
                              <Typography variant="caption" color="text.secondary">{h.duration_days}d</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{h.reason_code || '—'}</Typography>
                              {h.notes && <Typography variant="caption" color="text.secondary">{h.notes}</Typography>}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={h.source || 'manual'} variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{h.requested_by_email || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleCancel(h.id, { cancelBatch: isBatch, territoryCount: grp.members.length })}
                              >
                                {isBatch ? 'Cancel batch' : 'Cancel'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </Box>
  );
}
